import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Chip, Button } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useLanguage } from '../../../../context/LanguageContext';
import { Store } from '../../../../types/Store';
import { EditAdditionalInfoDrawer } from '../../UpdateStore/components/Drawers';

interface AdditionalInfoProps {
    store: Store;
    isEditable?: boolean;
    listStoresOptions?: any[];
}

const InfoItem = ({ label, value }: { label: string; value?: string | number | null }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">{value || '-'}</Typography>
    </Box>
);

const DependencySection = ({ title, items, color }: { 
    title: string; 
    items: Array<{ ENTITY_ID: string | number; IDENTIFIER: string; NAME: string }>; 
    color?: 'primary' | 'secondary' 
}) => (
    <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AccountTreeIcon sx={{ mr: 1, fontSize: '1.2rem', color: `${color}.main` }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>
        </Box>
        <Box sx={{ maxHeight: '200px', overflowY: 'auto', pl: 2 }}>
            {items.map((item, index) => (
                <Chip key={index} label={`${item.IDENTIFIER} - ${item.NAME}`} size="small" sx={{ mb: 1, mr: 1 }} variant="outlined" color={color} />
            ))}
        </Box>
    </Box>
);

const AdditionalInfo = ({ store, isEditable = false, listStoresOptions = [] }: AdditionalInfoProps) => {
    const { t } = useLanguage();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const formatDate = (dateString?: string) => dateString ? new Date(dateString).toLocaleDateString('fr-FR') : '-';
    const getCurrency = (currencyId?: string | number): string => (currencyId === '1' || currencyId === 1) ? 'Euro (€)' : currencyId?.toString() || '-';

    const isCompany = store.ENTITY_TYPE === 'COMPANY' || store.ENTITY_TYPE === 'ROOT';
    const hasParents = store.PARENT && store.PARENT.length > 0;
    const hasChildren = store.CHILDREN && store.CHILDREN.length > 0;
    const showDependencies = store.ENTITY_ID !== '1' && store.ENTITY_ID !== 1 && (hasParents || hasChildren);

    return (
        <>
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <InfoIcon sx={{ mr: 1, color: '#556ee6' }} />
                            <Typography variant="h6">{t('pages.store.additionalInfo')}</Typography>
                        </Box>
                        {isEditable && (
                            <Button 
                                size="small" 
                                variant="text"
                                color="primary" 
                                onClick={() => setDrawerOpen(true)}
                            >
                                {t('common.edit')}
                            </Button>
                        )}
                    </Box>
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                        <InfoItem label={t('pages.store.latitude')} value={store.LATITUDE} />
                    <InfoItem label={t('pages.store.longitude')} value={store.LONGITUDE} />
                    <InfoItem label={t('pages.store.currency')} value={getCurrency(store.CURRENCY_ID)} />
                    <InfoItem label={t('pages.store.subscriptionDate')} value={formatDate(store.SUBSCRIBE_DATE)} />
                    <InfoItem label={t('pages.store.unsubscriptionDate')} value={formatDate(store.UNSUBSCRIBE_DATE)} />
                    <InfoItem label={t('pages.store.signature')} value={store.SIGNATURE} />
                    <InfoItem label="RCS" value={store.RCS} />
                    <InfoItem label={t('pages.store.description')} value={store.DESCRIPTION} />
                    <InfoItem label={t('pages.store.openingHours')} value={store.OPENING_HOURS} />
                </Box>
                {showDependencies && (
                    <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: hasParents && hasChildren ? 'repeat(2, 1fr)' : '1fr' }, gap: 3 }}>
                            {!isCompany && hasParents && store.PARENT && (
                                <DependencySection title={t('pages.store.dependsOn')} items={store.PARENT} color="primary" />
                            )}
                            {hasChildren && store.CHILDREN && (
                                <DependencySection title={t('pages.store.groupedWith')} items={store.CHILDREN} color="secondary" />
                            )}
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
        
        <EditAdditionalInfoDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            store={store}
            listStoresOptions={listStoresOptions}
            isCompany={isCompany}
        />
        </>
    );
};

export default AdditionalInfo;
